export const FIRST_PAGE_LIMIT = {
  landscape: 900,
  portrait: 1300,
};

export const SUBSEQUENT_PAGE_LIMIT = {
  landscape: 2500,
  portrait: 3000,
};

// Maximum number of lines that can fit on a page
export const MAX_LINES_PER_PAGE = {
  landscape: {
    first: 11, // Balanced to force splitting without being too aggressive
    subsequent: 70, // Subsequent pages have more space
  },
  portrait: {
    first: 15,      // First page with image
    subsequent: 85, // Subsequent pages
  },
};

export interface ContentSplitOptions {
  isFirstPageContent?: boolean;
  isLandscape?: boolean;
}

/**
 * Count the number of lines in text (split by newlines)
 */
const countLines = (text: string): number => {
  if (!text) return 0;
  return text.split("\n").length;
};

export const splitLongContent = (
  content: string,
  options: ContentSplitOptions = {},
): string[] => {
  const { isFirstPageContent = false, isLandscape = false } = options;

  if (!content || !content.trim()) return [];

  // Character-based limits for regular text
  const charLimit = isFirstPageContent
    ? isLandscape
      ? FIRST_PAGE_LIMIT.landscape
      : FIRST_PAGE_LIMIT.portrait
    : isLandscape
      ? SUBSEQUENT_PAGE_LIMIT.landscape
      : SUBSEQUENT_PAGE_LIMIT.portrait;

  // Line-based limits for text with many newlines
  const lineLimit = isFirstPageContent
    ? isLandscape
      ? MAX_LINES_PER_PAGE.landscape.first
      : MAX_LINES_PER_PAGE.portrait.first
    : isLandscape
      ? MAX_LINES_PER_PAGE.landscape.subsequent
      : MAX_LINES_PER_PAGE.portrait.subsequent;

  const totalLines = countLines(content);

  // If content fits within BOTH limits, return as single chunk
  if (totalLines <= lineLimit && content.length <= charLimit) {
    return [content];
  }

  // Content needs splitting
  const chunks = [];
  let remaining = content;
  let isFirstChunk = isFirstPageContent;

  while (remaining.length > 0) {
    // Get limits for this chunk
    const maxChunkChars = isFirstChunk
      ? isLandscape
        ? FIRST_PAGE_LIMIT.landscape
        : FIRST_PAGE_LIMIT.portrait
      : isLandscape
        ? SUBSEQUENT_PAGE_LIMIT.landscape
        : SUBSEQUENT_PAGE_LIMIT.portrait;

    const maxChunkLines = isFirstChunk
      ? isLandscape
        ? MAX_LINES_PER_PAGE.landscape.first
        : MAX_LINES_PER_PAGE.portrait.first
      : isLandscape
        ? MAX_LINES_PER_PAGE.landscape.subsequent
        : MAX_LINES_PER_PAGE.portrait.subsequent;

    const remainingLines = countLines(remaining);
    const remainingAvgLineLength = remaining.length / remainingLines;
    const remainingIsLineHeavy = remainingAvgLineLength < 30;

    // If remaining content fits within both limits, take it all
    if (remainingLines <= maxChunkLines && remaining.length <= maxChunkChars) {
      const finalChunk = remaining.trim();
      if (finalChunk) {
        chunks.push(finalChunk);
      }
      break;
    }

    // Find split point - prioritize line-based for line-heavy content
    let splitIndex = 0;
    // Line-based split
    let lineSplitIndex = 0;
    let currentLineCount = 1;
    let lastValidLineSplit = 0;

    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i] === "\n") {
        if (currentLineCount < maxChunkLines) {
          lastValidLineSplit = i + 1;
          currentLineCount++;
        } else {
          lineSplitIndex = lastValidLineSplit;
          break;
        }
      }
    }

    // If we didn't exceed line limit, check if we have valid splits
    if (lineSplitIndex === 0 && lastValidLineSplit > 0) {
      lineSplitIndex = lastValidLineSplit;
    }

    // For line-heavy content, use line-based split if available
    if (remainingIsLineHeavy && lineSplitIndex > 0) {
      splitIndex = lineSplitIndex;
    } else {
      // Character-based split for regular content
      let charSplitIndex = Math.min(maxChunkChars, remaining.length);

      // Find natural break points within character limit
      const paragraphBreak = remaining.lastIndexOf("\n\n", charSplitIndex);
      if (paragraphBreak > charSplitIndex * 0.3) {
        charSplitIndex = paragraphBreak + 2;
      } else {
        const lineBreak = remaining.lastIndexOf("\n", charSplitIndex);
        if (lineBreak > charSplitIndex * 0.3) {
          charSplitIndex = lineBreak + 1;
        } else {
          const sentenceBreak = remaining.lastIndexOf(". ", charSplitIndex);
          if (sentenceBreak > charSplitIndex * 0.3) {
            charSplitIndex = sentenceBreak + 2;
          } else {
            const wordBreak = remaining.lastIndexOf(" ", charSplitIndex);
            if (wordBreak > charSplitIndex * 0.3) {
              charSplitIndex = wordBreak + 1;
            }
          }
        }
      }

      // Use whichever split is available (prefer char-based for regular content)
      if (charSplitIndex > 0) {
        splitIndex = charSplitIndex;
      } else if (lineSplitIndex > 0) {
        splitIndex = lineSplitIndex;
      } else {
        splitIndex = remaining.length;
      }
    }

    // Ensure we make progress
    if (splitIndex === 0) {
      splitIndex = Math.min(1, remaining.length);
    }

    // If we didn't exceed line limit, check if we have valid splits
    if (lineSplitIndex === 0 && lastValidLineSplit > 0) {
      lineSplitIndex = lastValidLineSplit;
    }

    // For line-heavy content, use line-based split if available
    if (remainingIsLineHeavy && lineSplitIndex > 0) {
      splitIndex = lineSplitIndex;
    } else {
      // Character-based split for regular content
      let charSplitIndex = Math.min(maxChunkChars, remaining.length);

      // Find natural break points within character limit
      const paragraphBreak = remaining.lastIndexOf("\n\n", charSplitIndex);
      if (paragraphBreak > charSplitIndex * 0.3) {
        charSplitIndex = paragraphBreak + 2;
      } else {
        const lineBreak = remaining.lastIndexOf("\n", charSplitIndex);
        if (lineBreak > charSplitIndex * 0.3) {
          charSplitIndex = lineBreak + 1;
        } else {
          const sentenceBreak = remaining.lastIndexOf(". ", charSplitIndex);
          if (sentenceBreak > charSplitIndex * 0.3) {
            charSplitIndex = sentenceBreak + 2;
          } else {
            const wordBreak = remaining.lastIndexOf(" ", charSplitIndex);
            if (wordBreak > charSplitIndex * 0.3) {
              charSplitIndex = wordBreak + 1;
            }
          }
        }
      }

      // Use whichever split is available (prefer char-based for regular content)
      if (charSplitIndex > 0) {
        splitIndex = charSplitIndex;
      } else if (lineSplitIndex > 0) {
        splitIndex = lineSplitIndex;
      } else {
        splitIndex = remaining.length;
      }
    }

    // Ensure we make progress
    if (splitIndex === 0) {
      splitIndex = Math.min(1, remaining.length);
    }

    // Extract the chunk
    const chunk = remaining.substring(0, splitIndex).trim();
    if (chunk) {
      chunks.push(chunk);
    }

    // Update remaining content
    remaining = remaining.substring(splitIndex).trim();
    isFirstChunk = false;
  }

  return chunks;
};
